import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { FileCheck, Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { api, errMsg, startLogin, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getPending } from "@/lib/pendingDoc";
import { toast } from "sonner";

const TITLES = { login: "Accedi a Burocrazy", register: "Crea il tuo account", forgot: "Password dimenticata" };

const Field = ({ id, label, ...p }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} className="h-11 rounded-xl" data-testid={`auth-${id}-input`} {...p} />
  </div>
);

const GoogleBtn = () => (
  <Button type="button" variant="outline" onClick={() => { track("google_click"); startLogin(); }} className="w-full h-11 rounded-full" data-testid="auth-google-btn">
    <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    Continua con Google
  </Button>
);

const Sent = ({ email, kind, onResend }) => (
  <div className="text-center" data-testid="auth-sent">
    <MailCheck className="h-12 w-12 mx-auto text-[#1E3A8A]" />
    <h1 className="mt-4 text-2xl font-semibold">Controlla la tua email</h1>
    <p className="mt-2 text-slate-600 text-sm">
      {kind === "register" ? "Ti abbiamo inviato un link per confermare l'account a " : "Se esiste un account, abbiamo inviato un link per reimpostare la password a "}
      <strong>{email}</strong>. Guarda anche nello spam.
    </p>
    {onResend && <button onClick={onResend} className="mt-4 text-sm underline text-[#1E3A8A]" data-testid="auth-resend-btn">Invia di nuovo</button>}
  </div>
);

export default function Auth() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [unverified, setUnverified] = useState(false);
  const [pending, setPending] = useState(null);
  useEffect(() => {
    track("auth_view");
    getPending().then((p) => {
      if (!p) return;
      setPending(p);
      if (!params.get("mode")) setMode("register");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (user) return <Navigate to="/app" replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setUnverified(false);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: f.email, password: f.password });
        setUser(data);
        navigate("/app", { replace: true });
      } else if (mode === "register") {
        track("signup_submit");
        const { data } = await api.post("/auth/register", { ...f, ref: localStorage.getItem("bz_ref") || null });
        localStorage.removeItem("bz_ref");
        toast.success("Benvenuto! Ti abbiamo inviato un'email per confermare l'indirizzo.");
        setUser(data);
        navigate("/app", { replace: true });
      } else {
        await api.post("/auth/forgot-password", { email: f.email });
        setSent("forgot");
      }
    } catch (err) {
      if (err?.response?.status === 403) setUnverified(true);
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const resend = async () => {
    await api.post("/auth/resend-verification", { email: f.email }).catch(() => {});
    toast.success("Email di conferma inviata");
  };
  const go = (m) => { setMode(m); setSent(null); setUnverified(false); };

  return (
    <div className="min-h-screen bg-[#F9F9F6] grid place-items-center p-6 pb-48 sm:pb-6" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-3xl border bg-white p-8 fade-up">
          {sent ? <Sent email={f.email} kind={sent} onResend={sent === "register" ? resend : null} /> : (
            <>
              <h1 className="text-2xl font-semibold" data-testid="auth-title">{TITLES[mode]}</h1>
              {pending && mode !== "forgot" && (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="auth-pending-notice">
                  <FileCheck className="h-4 w-4 shrink-0" /> La tua lettera «{pending.name}» è pronta: la analizziamo appena entri.
                </p>
              )}
              {mode === "forgot" && <p className="mt-1 text-sm text-slate-600">Inserisci la tua email: ti mandiamo un link per sceglierne una nuova.</p>}
              {mode !== "forgot" && <div className="mt-6"><GoogleBtn /></div>}
              {mode !== "forgot" && <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />oppure con email<span className="h-px flex-1 bg-slate-200" /></div>}
              <form onSubmit={submit} className={`space-y-4 ${mode === "forgot" ? "mt-6" : ""}`} data-testid="auth-form">
                {mode === "register" && <Field id="name" label="Nome" value={f.name} onChange={set("name")} required autoComplete="name" />}
                <Field id="email" label="Email" type="email" value={f.email} onChange={set("email")} required autoComplete="email" />
                {mode !== "forgot" && <Field id="password" label="Password" type="password" value={f.password} onChange={set("password")} required minLength={mode === "register" ? 8 : 1} autoComplete={mode === "register" ? "new-password" : "current-password"} placeholder={mode === "register" ? "Almeno 8 caratteri" : ""} />}
                {mode === "login" && <button type="button" onClick={() => go("forgot")} className="text-sm text-[#1E3A8A] underline" data-testid="auth-forgot-link">Password dimenticata?</button>}
                {unverified && <button type="button" onClick={resend} className="block text-sm text-[#E07A5F] underline" data-testid="auth-unverified-resend">Invia di nuovo l'email di conferma</button>}
                <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#1E3A8A] hover:bg-[#172f70]" data-testid="auth-submit-btn">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {mode === "login" ? "Accedi" : mode === "register" ? "Crea account" : "Invia link"}
                </Button>
              </form>
              {mode === "register" && <p className="mt-4 text-xs text-slate-500">Continuando accetti i <Link to="/termini" className="underline">Termini</Link> e l'<Link to="/privacy" className="underline">Informativa privacy</Link>.</p>}
            </>
          )}
          <p className="mt-6 text-sm text-center text-slate-600">
            {mode === "login" ? <>Non hai un account? <button onClick={() => go("register")} className="text-[#1E3A8A] font-medium underline" data-testid="auth-to-register">Registrati</button></>
              : <>Hai già un account? <button onClick={() => go("login")} className="text-[#1E3A8A] font-medium underline" data-testid="auth-to-login">Accedi</button></>}
          </p>
        </div>
      </div>
    </div>
  );
}
